import { getApiUrl, getAuthHeaders, handleApiResponse, API_CONFIG } from '../config/api';

export interface Category {
  id: number;
  name: string;
  description?: string;
  status: 'active' | 'inactive' | 'pending';
  isActive: boolean;
  sortOrder?: number;
  image?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryData {
  name: string;
  description?: string;
  status?: 'active' | 'inactive' | 'pending';
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateCategoryData {
  name?: string;
  description?: string;
  status?: 'active' | 'inactive' | 'pending';
  isActive?: boolean;
  sortOrder?: number;
}

export interface GetCategoriesParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface CategoriesResponse {
  success: boolean;
  data: {
    categories: Category[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface CategoryResponse {
  success: boolean;
  data: Category;
  message?: string;
}

class CategoryService {

  async getCategories(params: GetCategoriesParams = {}): Promise<CategoriesResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);

    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_CATEGORIES}?${queryParams}`), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    return response.json();
  }

  async getCategory(id: number): Promise<CategoryResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORY_BY_ID(id)), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch category');
    }

    return response.json();
  }

  async createCategory(data: CreateCategoryData, imageFile?: File | null): Promise<CategoryResponse> {
    const formData = new FormData();
    console.log({data});
    console.log({imageFile});
    
    // Add all category data to FormData
    // Object.entries(data).forEach(([key, value]) => {
    //   if (value !== undefined && value !== null) {
    //     formData.append(key, value.toString());
    //   }
    // });
    
    // Add image file if provided
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const token = localStorage.getItem('admin_token');
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORIES), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });


    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create category');
    }

    return response.json();
  }

  async updateCategory(id: number, data: UpdateCategoryData, imageFile?: File | null): Promise<CategoryResponse> {
    const formData = new FormData();
    console.log({data});
    console.log({imageFile});
    // Add all category data to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });
    
    // Add image file if provided
    if (imageFile) {
      formData.append('image', imageFile);
    }

  const token = localStorage.getItem('admin_token');

    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORY_BY_ID(id)), {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update category');
    }

    return response.json();
  }

  async deleteCategory(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORY_BY_ID(id)), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete category');
    }

    return response.json();
  }

  async updateCategoryStatus(id: number, status: 'active' | 'inactive' | 'pending'): Promise<CategoryResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORY_STATUS(id)), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update category status');
    }

    return response.json();
  }

  async bulkUpdateCategories(categoryIds: number[], updates: UpdateCategoryData): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_CATEGORIES_BULK), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ categoryIds, updates }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to bulk update categories');
    }

    return response.json();
  }
}

export const categoryService = new CategoryService();
